import { useTranslation } from 'next-i18next';
import { getLocalizedRegionName } from '../regions';
import formatCurrency from '../formatCurrency';
import formatDate from '../formatDate';
import formatHectares from '../formatHectares';
import formatNumber, { formatMaybeNumberString } from '../formatNumber';
import { yesNoUndefined, yesOrNull } from '../utils';
import { DataSection, UsePropertyInfoProps } from './types';
import { getComputedSection, getComputedItem, isNonEmptyValue } from './utils';

// eslint-disable-next-line import/prefer-default-export
export const usePropertyInfo = <T>({ processor }: UsePropertyInfoProps<T>) => {
  const { t, i18n } = useTranslation('generated_pdfs');
  const locale = i18n.language;

  const propertyBasicInfo: DataSection = {
    title: t('property_information', 'Kiinteistön tiedot'),
    items: [
      {
        name: 'property_name',
      },
      {
        name: 'property_id',
      },
      {
        name: 'property_address',
        value: (p) =>
          [
            p.property_address,
            p.property_postal_code,
            getLocalizedRegionName(p.property_municipality, locale),
          ]
            .filter(Boolean)
            .join(', '),
        label: t('address', 'Osoite'),
      },
      {
        name: 'property_type',
        value: (p) =>
          t<string>(
            `backoffice:property_type_values.${p.property_type as string}`,
          ),
        label: t('property_type', 'Kiinteistötyyppi'),
      },
      {
        name: 'property_sub_type',
        value: (p) =>
          [
            t<string>(
              `backoffice:property_sub_type_values.${
                p.property_sub_type as string
              }`,
            ),
            p.property_sub_type === 'share'
              ? p.property_co_owned_forest_share
              : '',
          ].join(' '),
        label: t('property_sub_type', 'Kiinteistötyypin tarkennus'),
      },
      {
        name: 'has_buildings',
        value: (p) => yesOrNull(p.has_buildings, t),
        label: t('generated_pdfs:has_buildings', 'Sisältää rakennuksia'),
      },
    ],
  };

  const propertyGroundInfo: DataSection = {
    title: t('generated_pdfs:ground', 'Maapohja'),
    items: [
      {
        name: 'property_area_is_owned',
        value: (p) => yesOrNull(p.property_area_is_owned, t),
      },
      {
        name: 'property_area_is_rented',
        value: (p) => yesOrNull(p.property_area_is_rented, t),
        items: [
          {
            name: 'property_area_amount_of_rent_per_year_in_euros',
            value: (p) =>
              formatCurrency(
                p.property_area_amount_of_rent_per_year_in_euros as number,
                {
                  locale,
                  fallbackValue: '',
                },
              ),
          },
          {
            name: 'property_area_lease_end_date',
            value: (p) =>
              p.property_area_lease_end_date
                ? formatDate(p.property_area_lease_end_date as string, {
                    locale,
                    fallbackValue: '',
                  })
                : null,
          },
          {
            name: 'property_area_lease_transfer_limited',
            value: (p) => yesOrNull(p.property_area_lease_transfer_limited, t),
          },
        ],
      },
      {
        name: 'property_area_rented_to_3rd_party',
        value: (p) => yesOrNull(p.property_area_rented_to_3rd_party, t),
        items: [
          {
            name: 'property_area_rented_to_3rd_party_description',
          },
          {
            name: 'property_area_rented_to_3rd_party_rent_per_year_in_euros',
            value: (p) =>
              formatCurrency(
                p.property_area_rented_to_3rd_party_rent_per_year_in_euros as number,
                { locale, fallbackValue: '' },
              ),
            label: t('property_area_amount_of_rent_per_year_in_euros'),
          },
          {
            name: 'property_area_rented_to_3rd_party_lease_end_date',
            value: (p) =>
              p.property_area_rented_to_3rd_party_lease_end_date
                ? formatDate(
                    p.property_area_rented_to_3rd_party_lease_end_date as string,
                    { locale, fallbackValue: '' },
                  )
                : null,
            label: t('property_area_lease_end_date'),
          },
        ],
      },
      {
        name: 'property_area_restriction_on_use_or_transfer_in_the_area',
        value: (p) =>
          yesOrNull(
            p.property_area_restriction_on_use_or_transfer_in_the_area,
            t,
          ),
        label: t('property_area_restriction_on_use_or_transfer_in_the_area'),
        description: (p) =>
          p.property_area_restriction_on_use_or_transfer_description,
      },
      {
        name: 'property_area_in_hectares',
        value: (p) =>
          p.property_area_in_hectares
            ? formatHectares(p.property_area_in_hectares as number, {
                locale,
                fallbackValue: '',
                approximate: p.has_approximate_area,
              })
            : null,
        label: t('property_area_in_hectares'),
      },
      {
        name: 'property_area_additional_information',
        value: (p) => p.property_area_additional_information,
        label: t('backoffice:property_area_additional_information'),
      },
    ],
  };

  const propertyWaterAreaInfo: DataSection = {
    title: t('generated_pdfs:water_area', 'Vesialue'),
    items: [
      {
        name: 'water_area_in_hectares',
        value: (p) =>
          p.water_area_in_hectares
            ? formatHectares(p.water_area_in_hectares as number, {
                locale,
                fallbackValue: '',
              })
            : null,
        label: t('backoffice:water_area_in_hectares'),
      },
      {
        name: 'property_has_shoreline',
        value: (p) => yesOrNull(p.property_has_shoreline, t),
        label: t('backoffice:property_has_shoreline'),
        description: (p) =>
          [
            [
              formatMaybeNumberString(p.property_shoreline_in_meters, {
                unit: 'm',
              }),
              p.property_shoreline_type &&
                t(
                  `property_shoreline_type_${p.property_shoreline_type}`,
                  p.property_shoreline_type,
                ).toLowerCase(),
            ]
              .filter(Boolean)
              .join(', '),
            p.property_shoreline_description || '',
          ]
            .filter(Boolean)
            .join('. '),
      },
      {
        name: 'property_reliction_area',
        value: (p) => p.property_reliction_area,
        label: t('backoffice:property_reliction_area'),
      },
      {
        name: 'property_has_shore_right',
        value: (p) => yesOrNull(p.property_has_shore_right, t),
        label: t('backoffice:property_has_shore_right'),
      },
    ],
  };

  const propertyLandUsePlanningInfo: DataSection = {
    title: t(
      'generated_pdfs:land_use_planning_and_other_information',
      'Kaavoitus ja muut tiedot',
    ),
    items: [
      {
        name: 'property_plan_none',
        value: (p) =>
          p.property_plan_none
            ? t('backoffice:property_plan_none').toString()
            : null,
        label: t('generated_pdfs:property_plan'),
      },
      {
        name: 'property_plan_local',
        value: (p) => yesOrNull(p.property_plan_local, t),
        label: t('backoffice:property_plan_local'),
        items: [
          {
            name: 'property_plan_local_description',
            label: t('backoffice:property_plan_description'),
          },
          {
            name: 'property_plan_local_url',
            label: t('backoffice:property_plan_url'),
          },
        ],
      },
      {
        name: 'property_plan_shoreline',
        value: (p) => yesOrNull(p.property_plan_shoreline, t),
        label: t('backoffice:property_plan_shoreline'),
        items: [
          {
            name: 'property_plan_shoreline_description',
            label: t('backoffice:property_plan_description'),
          },
          {
            name: 'property_plan_shoreline_url',
            label: t('backoffice:property_plan_url'),
          },
        ],
      },
      {
        name: 'property_plan_master',
        value: (p) => yesOrNull(p.property_plan_master, t),
        label: t('backoffice:property_plan_master'),
        items: [
          {
            name: 'property_plan_master_description',
            label: t('backoffice:property_plan_description'),
          },
          {
            name: 'property_plan_master_url',
            label: t('backoffice:property_plan_url'),
          },
        ],
      },
      {
        name: 'property_plan_regional',
        value: (p) => yesOrNull(p.property_plan_regional, t),
        label: t('backoffice:property_plan_regional'),
        items: [
          {
            name: 'property_plan_regional_description',
            label: t('backoffice:property_plan_description'),
          },
          {
            name: 'property_plan_regional_url',
            label: t('backoffice:property_plan_url'),
          },
        ],
      },
      {
        name: 'property_plan_development_area',
        value: (p) => yesOrNull(p.property_plan_development_area, t),
        label: t('backoffice:property_plan_development_area'),
      },
      {
        name: 'property_plan_building_permit',
        value: (p) => yesOrNull(p.property_plan_building_permit, t),
        label: t('backoffice:property_plan_building_permit'),
      },
      {
        name: 'property_building_ban',
        value: (p) => yesOrNull(p.property_building_ban, t),
        label: t('backoffice:property_building_ban'),
      },
      {
        name: 'property_prohibition_of_action',
        value: (p) => yesOrNull(p.property_prohibition_of_action, t),
        label: t('backoffice:property_prohibition_of_action'),
      },
      {
        name: 'property_plan_wind_farm',
        value: (p) => yesOrNull(p.property_plan_wind_farm, t),
        label: t('backoffice:property_plan_wind_farm'),
      },
      {
        name: 'property_plans_and_prohibitions_other',
        value: (p) => p.property_plans_and_prohibitions_other,
        label: t(
          'backoffice:property_plans_and_prohibitions_other',
          'Muut lisätiedot kaavoitukseen liittyen',
        ),
      },
      {
        name: 'property_building_right_and_buildings',

        value: (p) => p.property_building_right_and_buildings,
        label: t('backoffice:property_building_right_and_buildings'),
      },
      {
        name: 'property_building_right_more_info_from',

        value: (p) => p.property_building_right_more_info_from,
        label: t('backoffice:property_building_right_more_info_from'),
      },
      {
        name: 'property_restrictions_on_use_and_or_assignment',

        value: (p) => p.property_restrictions_on_use_and_or_assignment,
        label: t('backoffice:property_restrictions_on_use_and_or_assignment'),
      },
    ],
  };

  const propertyWaterPipeInfo: DataSection = {
    title: t('generated_pdfs:water_pipe', 'Vesijohto'),
    items: [
      {
        name: 'property_has_water_pipe_own',
        value: (p) => yesOrNull(p.property_has_water_pipe_own, t),
        label: t('backoffice:property_has_water_pipe_own'),
      },

      {
        name: 'property_has_water_pipe_cooperative',
        value: (p) => yesOrNull(p.property_has_water_pipe_cooperative, t),
        label: t('backoffice:property_has_water_pipe_cooperative'),
      },

      {
        name: 'property_has_water_pipe_municipalty',
        value: (p) => yesOrNull(p.property_has_water_pipe_municipalty, t),
        label: t('backoffice:property_has_water_pipe_municipalty'),
      },

      {
        name: 'property_has_water_borehole',
        value: (p) => yesOrNull(p.property_has_water_borehole, t),
        label: t('backoffice:property_has_water_borehole'),
      },

      {
        name: 'property_has_water_well',
        value: (p) => yesOrNull(p.property_has_water_well, t),
        label: t('backoffice:property_has_water_well'),
      },

      {
        name: 'property_water_quality_and_reserves',
        value: (p) => p.property_water_quality_and_reserves,
        label: t('backoffice:property_water_quality_and_reserves'),
      },
    ],
  };

  const propertySewerInfo: DataSection = {
    title: t('generated_pdfs:sewer', 'Viemäri'),
    items: [
      {
        name: 'property_has_sewer_line_own',
        value: (p) => yesOrNull(p.property_has_sewer_line_own, t),
        label: t('backoffice:property_has_sewer_line_own'),
      },
      {
        name: 'property_has_sewer_line_cooperative',
        value: (p) => yesOrNull(p.property_has_sewer_line_cooperative, t),
        label: t('backoffice:property_has_sewer_line_cooperative'),
      },
      {
        name: 'property_has_sewer_line_municipality',
        value: (p) => yesOrNull(p.property_has_sewer_line_municipality, t),
        label: t('backoffice:property_has_sewer_line_municipality'),
      },
      {
        name: 'property_has_sewer_line_settling_well_and_filtering',
        value: (p) =>
          yesOrNull(p.property_has_sewer_line_settling_well_and_filtering, t),
        label: t(
          'backoffice:property_has_sewer_line_settling_well_and_filtering',
        ),
      },
      {
        name: 'property_has_sewer_line_closed_container',
        value: (p) => yesOrNull(p.property_has_sewer_line_closed_container, t),
        label: t('backoffice:property_has_sewer_line_closed_container'),
      },
      {
        name: 'property_has_sewer_additional_info',
        value: (p) => p.property_has_sewer_line_additional_info,
        label: t('backoffice:property_has_sewer_line_additional_info'),
      },
    ],
  };

  const propertyConnectionsInfo: DataSection = {
    title: t('generated_pdfs:connections', 'Liittymät'),
    items: [
      {
        name: 'property_has_a_road_to_destination',
        value: (p) => yesOrNull(p.property_has_a_road_to_destination, t),
        label: t('backoffice:property_has_a_road_to_destination'),
        description: (p) => p.property_has_a_road_to_destination_description,
      },
      {
        name: 'property_has_electrical_connection_transfers',
        value: (p) =>
          yesOrNull(p.property_has_electrical_connection_transfers, t),
        label: t('backoffice:property_has_electrical_connection_transfers'),
      },

      {
        name: 'property_has_other_connections_transferring',
        value: (p) =>
          yesOrNull(p.property_has_other_connections_transferring, t),
        label: t('backoffice:property_has_other_connections_transferring'),
        description: (p) =>
          p.property_has_other_connections_transferring_description,
      },
      {
        name: 'property_insurances_and_or_insurance_value',
        value: (p) => p.property_insurances_and_or_insurance_value,
        label: t('backoffice:property_insurances_and_or_insurance_value'),
      },
    ],
  };

  const propertyCostsInfo: DataSection = {
    title: t('generated_pdfs:costs', 'Kustannukset'),
    items: [
      {
        name: 'property_road_cost',
        value: (p) =>
          formatMaybeNumberString(p.property_road_cost, {
            unit: '€',
            maximumFractionDigits: 2,
          }),
        label: t('backoffice:property_road_cost'),
      },
      {
        name: 'property_road_cost_details',
        value: (p) => p.property_road_cost_details,
        label: t('generated_pdfs:property_road_cost_details'),
      },
      {
        name: 'property_tax_amount_in_euros',
        value: (p) =>
          formatCurrency(p.property_tax_amount_in_euros as number, {
            locale,
            fallbackValue: '',
          }),
        label: t('backoffice:property_tax'),
      },
      {
        name: 'property_other_costs',
        value: (p) => p.property_other_costs,
        label: t('generated_pdfs:property_other_costs'),
      },
    ],
  };

  const propertyDebtsAndMortgagesInfo: DataSection = {
    title: t(
      'generated_pdfs:encumbrances_and_easements',
      'Encumbrances, easements and rights',
    ),
    items: [
      {
        name: 'property_debt_mort_mortgages',
        value: (p) => p.property_debt_mort_mortgages,
        label: t('backoffice:property_debt_mort_mortgages'),
      },
      {
        name: 'property_debt_mort_servitudes',
        value: (p) => p.property_debt_mort_servitudes,
        label: t('backoffice:property_debt_mort_servitudes'),
      },
      {
        name: 'property_right_to_co_owned_areas',
        value: (p) => p.property_right_to_co_owned_areas,
        label: t('backoffice:property_right_to_co_owned_areas'),
      },
      {
        name: 'property_debt_mort_other_rights',
        value: (p) => p.property_debt_mort_other_rights,
        label: t('backoffice:property_debt_mort_other_rights'),
      },
      {
        name: 'property_debt_mort_unpaid_payments',
        value: (p) => p.property_debt_mort_unpaid_payments,
        label: t('backoffice:property_debt_mort_unpaid_payments'),
      },
      {
        name: 'property_debt_mort_all_non_registered_payments_debts',
        value: (p) => p.property_debt_mort_all_non_registered_payments_debts,
        label: t(
          'backoffice:property_debt_mort_all_non_registered_payments_debts',
        ),
      },
    ],
  };

  const propertyExtraInformationInfo: DataSection = {
    title: t('generated_pdfs:extra_information', 'Lisätiedot'),
    items: [
      {
        name: 'extra_information',
        value: (p) => p.extra_information,
        label: t('backoffice:important_extra_information'),
      },
      {
        name: 'extra_information_attachment',
        value: (p) => yesOrNull(p.extra_information_attachment, t),
        label: t('backoffice:extra_information_attachment'),
      },
    ],
  };

  const propertyDrivingInstructionsInfo: DataSection = {
    title: t('generated_pdfs:driving_instructions', 'Ajo-ohjeet'),
    items: [
      {
        name: 'driving_instructions',
        value: (p) => p.driving_instructions,
        label: t('backoffice:driving_instructions'),
      },
    ],
  };

  const propertyEnvironmentAndGroundInfo: DataSection = {
    title: t('generated_pdfs:enviroment_and_ground', 'Ympäristö ja maaperä'),
    items: [
      {
        name: 'has_not_owned_assets_on_property',
        value: (p) => yesOrNull(p.has_not_owned_assets_on_property, t),
        label: t('backoffice:has_not_owned_assets_on_property'),
        description: (p) => p.has_not_owned_assets_on_property_description,
      },
      {
        name: 'has_waste_or_hazardous_waste',
        value: (p) => yesOrNull(p.has_waste_or_hazardous_waste, t),
        label: t('backoffice:has_waste_or_hazardous_waste'),
        description: (p) => p.has_waste_or_hazardous_waste_description,
      },
      {
        name: 'has_had_polluting_activities',
        value: (p) => yesOrNull(p.has_had_polluting_activities, t),
        label: t('backoffice:has_had_polluting_activities'),
        description: (p) => p.has_had_polluting_activities_description,
      },
      {
        name: 'has_areas_in_need_of_afforestation',
        value: (p) => yesOrNull(p.has_areas_in_need_of_afforestation, t),
        label: t('backoffice:has_areas_in_need_of_afforestation'),
        description: (p) => p.has_areas_in_need_of_afforestation_description,
      },
      {
        name: 'has_reservation_area',
        value: (p) => yesOrNull(p.has_reservation_area, t),
        label: t('backoffice:has_reservation_area'),
        description: (p) => p.has_reservation_area_description,
      },
      {
        name: 'has_protected_species',
        value: (p) => yesOrNull(p.has_protected_species, t),
        label: t('backoffice:has_protected_species'),
        description: (p) => p.has_protected_species_description,
      },
      {
        name: 'has_objects_of_section_10_of_the_forest_act',
        value: (p) =>
          yesOrNull(p.has_objects_of_section_10_of_the_forest_act, t),
        label: t('backoffice:has_objects_of_section_10_of_the_forest_act'),
        description: (p) =>
          p.has_objects_of_section_10_of_the_forest_act_description,
      },
      {
        name: 'has_nature_conservation_law_areas',
        value: (p) => yesOrNull(p.has_nature_conservation_law_areas, t),
        label: t('backoffice:has_nature_conservation_law_areas'),
        description: (p) => p.has_nature_conservation_law_areas_description,
      },
      {
        name: 'has_groundwater_area',
        value: (p) => yesOrNull(p.has_groundwater_area, t),
        label: t('backoffice:has_groundwater_area'),
        description: (p) => p.has_groundwater_area_description,
      },
      {
        name: 'has_mining_area',
        value: (p) => yesOrNull(p.has_mining_area, t),
        label: t('backoffice:has_mining_area'),
        description: (p) => p.has_mining_area_description,
      },
      {
        name: 'has_eu_support_eligible_farming_land',
        value: (p) => yesOrNull(p.has_eu_support_eligible_farming_land, t),
        label: t('backoffice:has_eu_support_eligible_farming_land'),
        description: (p) => p.has_eu_support_eligible_farming_land_description,
      },
      {
        name: 'has_eu_support_eligible_farming_land_checked',
        value: (p) =>
          yesOrNull(p.has_eu_support_eligible_farming_land_checked, t),
        label: t('backoffice:has_eu_support_eligible_farming_land_checked'),
        description: (p) =>
          p.has_eu_support_eligible_farming_land_checked_description,
      },
      {
        name: 'has_hunting_lease_agreement',
        value: (p) => yesOrNull(p.has_hunting_lease_agreement, t),
        label: t('backoffice:has_hunting_lease_agreement'),
        description: (p) => p.hunting_lease_agreement_club_name,
      },
    ],
  };

  const propertyForestDamageInfo: DataSection = {
    title: t('generated_pdfs:forest_damage', 'Metsätuhot'),
    items: [
      {
        name: 'has_storm_or_insect_damage',
        value: (p) => yesNoUndefined(p.has_storm_or_insect_damage, t, ''),
        label: t('backoffice:has_storm_or_insect_damage'),
        description: (p) => p.has_storm_or_insect_damage_description,
      },
    ],
  };

  const buildingBasicInfo: DataSection = {
    title: t('building_information', 'Asuinrakennuksen perustiedot'),
    items: [
      {
        name: 'habitable_buildings.0.building_vacancy_info',
        label: t('building_vacancy_info'),
      },
      {
        name: 'habitable_buildings.0.building_control_sharing_agreement',
        value: (p) =>
          yesOrNull(
            p.habitable_buildings[0]?.building_control_sharing_agreement,
            t,
          ),
        label: t('building_control_sharing_agreement'),
        description: (p) =>
          p.habitable_buildings[0]
            ?.building_control_sharing_agreement_description,
      },
      {
        name: 'habitable_buildings.0.building_construction_year',
        label: t('building_construction_year'),
      },
      {
        name: 'habitable_buildings.0.building_introduction_year',
        label: t('building_introduction_year'),
      },
      {
        name: 'habitable_buildings.0.building_commissioning_inspection',
        value: (p) =>
          yesOrNull(
            p.habitable_buildings[0]?.building_commissioning_inspection,
            t,
          ),
        label: t('building_commissioning_inspection'),
      },
      {
        name: 'habitable_buildings.0.building_final_inspection_date',
        value: (p) =>
          yesOrNull(
            !!p.habitable_buildings[0]?.building_final_inspection_date,
            t,
          ),
        label: t('building_final_inspection_date'),
        description: (p) =>
          formatDate(
            p.habitable_buildings[0]?.building_final_inspection_date as string,
            {
              locale,
              fallbackValue: '',
            },
          ),
      },
      {
        name: 'habitable_buildings.0.building_material',
        label: t('building_material'),
      },
      {
        name: 'habitable_buildings.0.building_floor_area_in_square_meters',
        value: (p) =>
          formatNumber(
            p.habitable_buildings[0]?.building_floor_area_in_square_meters,
            { locale, fallbackValue: '', unit: 'm²' },
          ),
        label: t('building_floor_area_in_square_meters'),
      },
      {
        name: 'habitable_buildings.0.building_area_living_square_meters',
        value: (p) =>
          formatNumber(
            p.habitable_buildings[0]?.building_area_living_square_meters,
            { locale, fallbackValue: '', unit: 'm²' },
          ),
        label: t('building_area_living_square_meters'),
      },
      {
        name: 'habitable_buildings.0.building_area_other_square_meters',
        value: (p) =>
          formatNumber(
            p.habitable_buildings[0]?.building_area_other_square_meters,
            { locale, fallbackValue: '', unit: 'm²' },
          ),
        label: t('building_area_other_square_meters'),
      },
      {
        name: 'habitable_buildings.0.building_area_total_square_meters',
        value: (p) =>
          formatNumber(
            p.habitable_buildings[0]?.building_area_total_square_meters,
            { locale, fallbackValue: '', unit: 'm²' },
          ),
        label: t('building_area_total_square_meters'),
      },
      {
        name: 'habitable_buildings.0.building_room_division',
        label: t('building_room_division'),
      },
      {
        name: 'habitable_buildings.0.building_area_inspection_measured',
        value: (p) =>
          yesOrNull(
            p.habitable_buildings[0]?.building_area_inspection_measured,
            t,
          ),
        label: t('building_area_inspection_measured'),
      },
      {
        name: 'habitable_buildings.0.building_area_additional_information',
        label: t('building_area_additional_information'),
      },
      {
        name: 'habitable_buildings.0.building_airconditioning_type',
        label: t('building_airconditioning_type'),
      },
      {
        name: 'habitable_buildings.0.building_heating_type',
        label: t('building_heating_type'),
      },
      {
        name: 'habitable_buildings.0.building_roof_type_underlay_material_and_condition',
        label: t('building_roof_type_underlay_material_and_condition'),
      },
    ],
  };

  const buildingKitchenInfo: DataSection = {
    title: t('kitchen', 'Keittiö'),
    items: [
      {
        name: 'habitable_buildings.0.kitchen_has_refrigerator',
        value: (p) =>
          yesNoUndefined(
            p.habitable_buildings[0]?.kitchen_has_refrigerator,
            t,
            '',
          ),
        label: t('kitchen_has_refrigerator'),
        description: (p) =>
          p.habitable_buildings[0]?.kitchen_refrigerator_description,
      },
      {
        name: 'habitable_buildings.0.kitchen_has_freezer',
        value: (p) =>
          yesNoUndefined(p.habitable_buildings[0]?.kitchen_has_freezer, t, ''),
        label: t('kitchen_has_freezer'),
        description: (p) =>
          p.habitable_buildings[0]?.kitchen_freezer_description,
      },
      {
        name: 'habitable_buildings.0.kitchen_has_refrigerator_cellar',
        value: (p) =>
          yesNoUndefined(
            p.habitable_buildings[0]?.kitchen_has_refrigerator_cellar,
            t,
            '',
          ),
        label: t('kitchen_has_refrigerator_cellar'),
        description: (p) =>
          p.habitable_buildings[0]?.kitchen_refrigerator_cellar_description,
      },
      {
        name: 'habitable_buildings.0.kitchen_has_refrigerator_room',
        value: (p) =>
          yesNoUndefined(
            p.habitable_buildings[0]?.kitchen_has_refrigerator_room,
            t,
            '',
          ),
        label: t('kitchen_has_refrigerator_room'),
        description: (p) =>
          p.habitable_buildings[0]?.kitchen_refrigerator_room_description,
      },
      {
        name: 'habitable_buildings.0.kitchen_has_cuboards',
        value: (p) =>
          yesNoUndefined(p.habitable_buildings[0]?.kitchen_has_cuboards, t, ''),
        label: t('kitchen_has_cuboards'),
        description: (p) =>
          p.habitable_buildings[0]?.kitchen_cuboards_description,
      },
      {
        name: 'habitable_buildings.0.kitchen_has_stove',
        value: (p) =>
          yesNoUndefined(p.habitable_buildings[0]?.kitchen_has_stove, t, ''),
        label: t('kitchen_has_stove'),
        description: (p) => p.habitable_buildings[0]?.kitchen_stove_description,
      },
      {
        name: 'habitable_buildings.0.kitchen_has_cooker_hood',
        value: (p) =>
          yesNoUndefined(
            p.habitable_buildings[0]?.kitchen_has_cooker_hood,
            t,
            '',
          ),
        label: t('kitchen_has_cooker_hood'),
        description: (p) =>
          p.habitable_buildings[0]?.kitchen_cooker_hood_desctiption,
      },
      {
        name: 'habitable_buildings.0.kitchen_has_baking_oven',
        value: (p) =>
          yesNoUndefined(
            p.habitable_buildings[0]?.kitchen_has_baking_oven,
            t,
            '',
          ),
        label: t('kitchen_has_baking_oven'),
        description: (p) =>
          p.habitable_buildings[0]?.kitchen_baking_oven_description,
      },
      {
        name: 'habitable_buildings.0.kitchen_has_microwave',
        value: (p) =>
          yesNoUndefined(
            p.habitable_buildings[0]?.kitchen_has_microwave,
            t,
            '',
          ),
        label: t('kitchen_has_microwave'),
        description: (p) =>
          p.habitable_buildings[0]?.kitchen_microwave_description,
      },
      {
        name: 'habitable_buildings.0.kitchen_has_dishwasher',
        value: (p) =>
          yesNoUndefined(
            p.habitable_buildings[0]?.kitchen_has_dishwasher,
            t,
            '',
          ),
        label: t('kitchen_has_dishwasher'),
        description: (p) =>
          p.habitable_buildings[0]?.kitchen_dishwasher_description,
      },
      {
        name: 'habitable_buildings.0.kitcher_additional_equipment_description',
        label: t('kitcher_additional_equipment_description'),
      },
    ],
  };

  const buildingBathroomInfo: DataSection = {
    title: t('bathrooms', 'Pesutilat'),
    items: [
      {
        name: 'habitable_buildings.0.sanitary_facilities_has_sauna',
        value: (p) =>
          yesNoUndefined(
            p.habitable_buildings[0]?.sanitary_facilities_has_sauna,
            t,
            '',
          ),
        label: t('sanitary_facilities_has_sauna'),
        description: (p) =>
          p.habitable_buildings[0]?.sanitary_facilities_sauna_description,
      },
      {
        name: 'habitable_buildings.0.sanitary_facilities_has_bathroom',
        value: (p) =>
          yesNoUndefined(
            p.habitable_buildings[0]?.sanitary_facilities_has_bathroom,
            t,
            '',
          ),
        label: t('sanitary_facilities_has_bathroom'),
        description: (p) =>
          p.habitable_buildings[0]?.sanitary_facilities_bathroom_description,
      },
      {
        name: 'habitable_buildings.0.sanitary_facilities_has_separate_toilet',
        value: (p) =>
          yesNoUndefined(
            p.habitable_buildings[0]?.sanitary_facilities_has_separate_toilet,
            t,
            '',
          ),
        label: t('sanitary_facilities_has_separate_toilet'),
        description: (p) =>
          p.habitable_buildings[0]
            ?.sanitary_facilities_separate_toilet_description,
      },
      {
        name: 'habitable_buildings.0.sanitary_facilities_has_shower',
        value: (p) =>
          yesNoUndefined(
            p.habitable_buildings[0]?.sanitary_facilities_has_shower,
            t,
            '',
          ),
        label: t('sanitary_facilities_has_shower'),
        description: (p) =>
          p.habitable_buildings[0]?.sanitary_facilities_shower_description,
      },
      {
        name: 'habitable_buildings.0.sanitary_facilities_has_bath_tub',
        value: (p) =>
          yesNoUndefined(
            p.habitable_buildings[0]?.sanitary_facilities_has_bath_tub,
            t,
            '',
          ),
        label: t('sanitary_facilities_has_bath_tub'),
        description: (p) =>
          p.habitable_buildings[0]?.sanitary_facilities_bath_tub_description,
      },
      {
        name: 'habitable_buildings.0.sanitary_facilities_has_mirrors',
        value: (p) =>
          yesNoUndefined(
            p.habitable_buildings[0]?.sanitary_facilities_has_mirrors,
            t,
            '',
          ),
        label: t('sanitary_facilities_has_mirrors'),
        description: (p) =>
          p.habitable_buildings[0]?.sanitary_facilities_mirrors_description,
      },
      {
        name: 'habitable_buildings.0.sanitary_facilities_has_mirror_cabinet',
        value: (p) =>
          yesNoUndefined(
            p.habitable_buildings[0]?.sanitary_facilities_has_mirror_cabinet,
            t,
            '',
          ),
        label: t('sanitary_facilities_has_mirror_cabinet'),
        description: (p) =>
          p.habitable_buildings[0]
            ?.sanitary_facilities_mirror_cabinet_description,
      },
      {
        name: 'habitable_buildings.0.sanitary_facilities_has_washing_machine_connection',
        value: (p) =>
          yesNoUndefined(
            p.habitable_buildings[0]
              ?.sanitary_facilities_has_washing_machine_connection,
            t,
            '',
          ),
        label: t('sanitary_facilities_has_washing_machine_connection'),
        description: (p) =>
          p.habitable_buildings[0]
            ?.sanitary_facilities_washing_machine_connection_description,
      },
      {
        name: 'habitable_buildings.0.sanitary_facilities_has_floor_heating',
        value: (p) =>
          yesNoUndefined(
            p.habitable_buildings[0]?.sanitary_facilities_has_floor_heating,
            t,
            '',
          ),
        label: t('sanitary_facilities_has_floor_heating'),
        description: (p) =>
          p.habitable_buildings[0]
            ?.sanitary_facilities_floor_heating_description,
      },
      {
        name: 'habitable_buildings.0.sanitary_facilities_kitcher_additional_equipment_description',
        label: t(
          'sanitary_facilities_kitcher_additional_equipment_description',
        ),
      },
    ],
  };

  const buildingSurfacesInfo: DataSection = {
    title: t('surfaces_and_materials', 'Pinnat ja materiaalit'),
    items: [
      {
        name: 'habitable_buildings.0.materials_walls_kitchen',
        value: (p) =>
          [
            p.habitable_buildings[0]?.materials_walls_kitchen &&
              `${t('walls', 'Seinämateriaalit')}: ${
                p.habitable_buildings[0]?.materials_walls_kitchen
              }`,
            p.habitable_buildings[0]?.materials_floors_kitchen &&
              `${t('floors', 'Lattiamateriaalit')}: ${
                p.habitable_buildings[0]?.materials_floors_kitchen
              }`,
            p.habitable_buildings[0]?.worth_noting_kitchen &&
              `${t('other_information', 'Muuta huomioitavaa')}: ${
                p.habitable_buildings[0]?.worth_noting_kitchen || '-'
              }`,
          ]
            .filter((v) => v && v.trim())
            .join('\n'),
        label: t('kitchen'),
      },
      {
        name: 'habitable_buildings.0.materials_walls_sauna_and_bathroom',
        value: (p) =>
          [
            p.habitable_buildings[0]?.materials_walls_sauna_and_bathroom &&
              `${t('walls', 'Seinämateriaalit')}: ${
                p.habitable_buildings[0]?.materials_walls_sauna_and_bathroom
              }`,
            p.habitable_buildings[0]?.materials_floors_sauna_and_bathroom &&
              `${t('floors', 'Lattiamateriaalit')}: ${
                p.habitable_buildings[0]?.materials_floors_sauna_and_bathroom
              }`,
            p.habitable_buildings[0]?.worth_noting_sauna_and_bathroom &&
              `${t('other_information', 'Muuta huomioitavaa')}: ${
                p.habitable_buildings[0]?.worth_noting_sauna_and_bathroom
              }`,
          ]
            .filter((v) => v && v.trim())
            .join('\n'),
        label: t('sauna_and_bathroom'),
      },
      {
        name: 'habitable_buildings.0.materials_walls_living_room',
        value: (p) =>
          [
            p.habitable_buildings[0]?.materials_walls_living_room &&
              `${t('walls', 'Seinämateriaalit')}: ${
                p.habitable_buildings[0]?.materials_walls_living_room
              }`,
            p.habitable_buildings[0]?.materials_floors_living_room &&
              `${t('floors', 'Lattiamateriaalit')}: ${
                p.habitable_buildings[0]?.materials_floors_living_room
              }`,
            p.habitable_buildings[0]?.worth_noting_living_room &&
              `${t('other_information', 'Muuta huomioitavaa')}: ${
                p.habitable_buildings[0]?.worth_noting_living_room
              }`,
          ]
            .filter((v) => v && v.trim())
            .join('\n'),
        label: t('living_room'),
      },
      {
        name: 'habitable_buildings.0.materials_walls_bedroom_1',
        value: (p) =>
          [
            p.habitable_buildings[0]?.materials_walls_bedroom_1 &&
              `${t('walls', 'Seinämateriaalit')}: ${
                p.habitable_buildings[0]?.materials_walls_bedroom_1
              }`,
            p.habitable_buildings[0]?.materials_floors_bedroom_1 &&
              `${t('floors', 'Lattiamateriaalit')}: ${
                p.habitable_buildings[0]?.materials_floors_bedroom_1
              }`,
            p.habitable_buildings[0]?.worth_noting_bedroom_1 &&
              `${t('other_information', 'Muuta huomioitavaa')}: ${
                p.habitable_buildings[0]?.worth_noting_bedroom_1
              }`,
          ]
            .filter((v) => v && v.trim())
            .join('\n'),
        label: t('bedroom_1', 'Makuuhuone 1'),
      },
      {
        name: 'habitable_buildings.0.materials_walls_bedroom_2',
        value: (p) =>
          [
            p.habitable_buildings[0]?.materials_walls_bedroom_2 &&
              `${t('walls', 'Seinämateriaalit')}: ${
                p.habitable_buildings[0]?.materials_walls_bedroom_2
              }`,
            p.habitable_buildings[0]?.materials_floors_bedroom_2 &&
              `${t('floors', 'Lattiamateriaalit')}: ${
                p.habitable_buildings[0]?.materials_floors_bedroom_2
              }`,
            p.habitable_buildings[0]?.worth_noting_bedroom_2 &&
              `${t('other_information', 'Muuta huomioitavaa')}: ${
                p.habitable_buildings[0]?.worth_noting_bedroom_2
              }`,
          ]
            .filter((v) => v && v.trim())
            .join('\n'),
        label: t('bedroom_2'),
      },
      {
        name: 'habitable_buildings.0.materials_walls_bedroom_3',
        value: (p) =>
          [
            p.habitable_buildings[0]?.materials_walls_bedroom_3 &&
              `${t('walls', 'Seinämateriaalit')}: ${
                p.habitable_buildings[0]?.materials_walls_bedroom_3
              }`,
            p.habitable_buildings[0]?.materials_floors_bedroom_3 &&
              `${t('floors', 'Lattiamateriaalit')}: ${
                p.habitable_buildings[0]?.materials_floors_bedroom_3
              }`,
            p.habitable_buildings[0]?.worth_noting_bedroom_3 &&
              `${t('other_information', 'Muuta huomioitavaa')}: ${
                p.habitable_buildings[0]?.worth_noting_bedroom_3
              }`,
          ]
            .filter((v) => v && v.trim())
            .join('\n'),
        label: t('bedroom_3'),
      },
      {
        name: 'habitable_buildings.0.materials_walls_utility_room',
        value: (p) =>
          [
            p.habitable_buildings[0]?.materials_walls_utility_room &&
              `${t('walls', 'Seinämateriaalit')}: ${
                p.habitable_buildings[0]?.materials_walls_utility_room
              }`,
            p.habitable_buildings[0]?.materials_floors_utility_room &&
              `${t('floors', 'Lattiamateriaalit')}: ${
                p.habitable_buildings[0]?.materials_floors_utility_room
              }`,
            p.habitable_buildings[0]?.worth_noting_utility_room &&
              `${t('other_information', 'Muuta huomioitavaa')}: ${
                p.habitable_buildings[0]?.worth_noting_utility_room
              }`,
          ]
            .filter((v) => v && v.trim())
            .join('\n'),
        label: t('utility_room'),
      },
      {
        name: 'habitable_buildings.0.materials_walls_hallway',
        value: (p) =>
          [
            p.habitable_buildings[0]?.materials_walls_hallway &&
              `${t('walls', 'Seinämateriaalit')}: ${
                p.habitable_buildings[0]?.materials_walls_hallway
              }`,
            p.habitable_buildings[0]?.materials_floors_hallway &&
              `${t('floors', 'Lattiamateriaalit')}: ${
                p.habitable_buildings[0]?.materials_floors_hallway
              }`,
            p.habitable_buildings[0]?.worth_noting_hallway &&
              `${t('other_information', 'Muuta huomioitavaa')}: ${
                p.habitable_buildings[0]?.worth_noting_hallway
              }`,
          ]
            .filter((v) => v && v.trim())
            .join('\n'),
        label: t('hallway'),
      },
      {
        name: 'habitable_buildings.0.materials_walls_extra',
        value: (p) =>
          [
            p.habitable_buildings[0]?.materials_walls_extra &&
              `${t('walls', 'Seinämateriaalit')}: ${
                p.habitable_buildings[0]?.materials_walls_extra
              }`,
            p.habitable_buildings[0]?.materials_floors_extra &&
              `${t('floors', 'Lattiamateriaalit')}: ${
                p.habitable_buildings[0]?.materials_floors_extra
              }`,
            p.habitable_buildings[0]?.worth_noting_extra &&
              `${t('other_information', 'Muuta huomioitavaa')}: ${
                p.habitable_buildings[0]?.worth_noting_extra
              }`,
          ]
            .filter((v) => v && v.trim())
            .join('\n'),
        label: t('other_rooms'),
      },
    ],
  };

  const buildingOtherInventoryInfo: DataSection = {
    title: t('other_belonging_to_trade', 'Muuta kauppaan kuuluvaa'),
    items: [
      {
        name: 'habitable_buildings.0.other_included_has_movable_wardrobes',
        value: (p) =>
          yesNoUndefined(
            p.habitable_buildings[0]?.other_included_has_movable_wardrobes,
            t,
            '',
          ),
        label: t('other_included_has_movable_wardrobes'),
        description: (p) =>
          p.habitable_buildings[0]
            ?.other_included_movable_wardrobes_description,
      },
      {
        name: 'habitable_buildings.0.other_included_has_lamps',
        value: (p) =>
          yesNoUndefined(
            p.habitable_buildings[0]?.other_included_has_lamps,
            t,
            '',
          ),
        label: t('other_included_has_lamps'),
        description: (p) =>
          p.habitable_buildings[0]?.other_included_lamps_description,
      },
      {
        name: 'habitable_buildings.0.other_included_has_blinders',
        value: (p) =>
          yesNoUndefined(
            p.habitable_buildings[0]?.other_included_has_blinders,
            t,
            '',
          ),
        label: t('other_included_has_blinders'),
        description: (p) =>
          p.habitable_buildings[0]?.other_included_blinders_description,
      },
      {
        name: 'habitable_buildings.0.other_included_has_coat_racks',
        value: (p) =>
          yesNoUndefined(
            p.habitable_buildings[0]?.other_included_has_coat_racks,
            t,
            '',
          ),
        label: t('other_included_has_coat_racks'),
        description: (p) =>
          p.habitable_buildings[0]?.other_included_coat_racks_description,
      },
      {
        name: 'habitable_buildings.0.other_included_has_drying_cabinet',
        value: (p) =>
          yesNoUndefined(
            p.habitable_buildings[0]?.other_included_has_drying_cabinet,
            t,
            '',
          ),
        label: t('other_included_has_drying_cabinet'),
        description: (p) =>
          p.habitable_buildings[0]?.other_included_drying_cabinet_description,
      },
      {
        name: 'habitable_buildings.0.other_included_has_dryer',
        value: (p) =>
          yesNoUndefined(
            p.habitable_buildings[0]?.other_included_has_dryer,
            t,
            '',
          ),
        label: t('other_included_has_dryer'),
        description: (p) =>
          p.habitable_buildings[0]?.other_included_dryer_description,
      },
      {
        name: 'habitable_buildings.0.other_included_has_central_vacuum_cleaner',
        value: (p) =>
          yesNoUndefined(
            p.habitable_buildings[0]?.other_included_has_central_vacuum_cleaner,
            t,
            '',
          ),
        label: t('other_included_has_central_vacuum_cleaner'),
        description: (p) =>
          p.habitable_buildings[0]
            ?.other_included_central_vacuum_cleaner_description,
      },
      {
        name: 'habitable_buildings.0.other_included_has_carpet_beating_rack',
        value: (p) =>
          yesNoUndefined(
            p.habitable_buildings[0]?.other_included_has_carpet_beating_rack,
            t,
            '',
          ),
        label: t('other_included_has_carpet_beating_rack'),
        description: (p) =>
          p.habitable_buildings[0]
            ?.other_included_carpet_beating_rack_description,
      },
      {
        name: 'habitable_buildings.0.other_included_has_clothes_drying_rack',
        value: (p) =>
          yesNoUndefined(
            p.habitable_buildings[0]?.other_included_has_clothes_drying_rack,
            t,
            '',
          ),
        label: t('other_included_has_clothes_drying_rack'),
        description: (p) =>
          p.habitable_buildings[0]
            ?.other_included_clothes_drying_rack_description,
      },
      {
        name: 'habitable_buildings.0.other_included_has_waste_container',
        value: (p) =>
          yesNoUndefined(
            p.habitable_buildings[0]?.other_included_has_waste_container,
            t,
            '',
          ),
        label: t('other_included_has_waste_container'),
        description: (p) =>
          p.habitable_buildings[0]?.other_included_waste_container_description,
      },
      {
        name: 'habitable_buildings.0.other_included_has_extra',
        value: (p) =>
          yesNoUndefined(
            p.habitable_buildings[0]?.other_included_has_extra,
            t,
            '',
          ),
        label: t('other_included_has_extra'),
        description: (p) =>
          p.habitable_buildings[0]?.other_included_extra_description,
      },
    ],
  };

  const buildingOtherEquipmentInfo: DataSection = {
    title: t('other_equipment', 'Muu varustus'),
    items: [
      {
        name: 'habitable_buildings.0.other_features_has_satellite_or_cable_tv',
        value: (p) =>
          yesOrNull(
            p.habitable_buildings[0]?.other_features_has_satellite_or_cable_tv,
            t,
          ),
        label: t('other_features_has_satellite_or_cable_tv'),
      },
      {
        name: 'habitable_buildings.0.other_features_has_fireplace',
        value: (p) =>
          yesOrNull(p.habitable_buildings[0]?.other_features_has_fireplace, t),
        label: t('other_features_has_fireplace'),
      },
      {
        name: 'habitable_buildings.0.other_features_has_heat_storing_fireplace',
        value: (p) =>
          yesOrNull(
            p.habitable_buildings[0]?.other_features_has_heat_storing_fireplace,
            t,
          ),
        label: t('other_features_has_heat_storing_fireplace'),
      },
      {
        name: 'habitable_buildings.0.other_features_has_yard_equipment',
        value: (p) =>
          yesNoUndefined(
            p.habitable_buildings[0]?.other_features_has_yard_equipment,
            t,
            '',
          ),
        label: t('other_features_has_yard_equipment'),
        description: (p) =>
          p.habitable_buildings[0]?.other_features_yard_equipment_description,
      },
      {
        name: 'habitable_buildings.0.other_features_has_yard_plantations',
        value: (p) =>
          yesNoUndefined(
            p.habitable_buildings[0]?.other_features_has_yard_plantations,
            t,
            '',
          ),
        label: t('other_features_has_yard_plantations'),
        description: (p) =>
          p.habitable_buildings[0]?.other_features_yard_plantations_description,
      },
      {
        name: 'habitable_buildings.0.other_features_has_extra',
        value: (p) =>
          yesNoUndefined(
            p.habitable_buildings[0]?.other_features_has_extra,
            t,
            '',
          ),
        label: t('other_features_has_extra'),
        description: (p) =>
          p.habitable_buildings[0]?.other_features_extra_description,
      },
    ],
  };

  const buildingDamagesAndRenovationsInfo: DataSection = {
    title: t('flaws_damages_and_renovations', 'Viat, vahingot ja korjaukset'),
    items: [
      {
        name: 'habitable_buildings.0.defects_building_condition_additional_information',
        value: (p) =>
          p.habitable_buildings[0]
            ?.defects_building_condition_additional_information,
        label: t('defects_building_condition_additional_information'),
      },
      {
        name: 'habitable_buildings.0.defects_has_humidity_measurement_done',
        value: (p) =>
          yesNoUndefined(
            p.habitable_buildings[0]?.defects_has_humidity_measurement_done,
            t,
            '',
          ),
        label: t('defects_has_humidity_measurement_done'),
        description: (p) =>
          formatDate(
            p.habitable_buildings[0]
              ?.defects_humidity_measurement_done_date as string,
            { locale, fallbackValue: '' },
          ),
      },
      {
        name: 'habitable_buildings.0.defects_has_condition_inspection_done',
        value: (p) =>
          yesNoUndefined(
            p.habitable_buildings[0]?.defects_has_condition_inspection_done,
            t,
            '',
          ),
        label: t('defects_has_condition_inspection_done'),
        description: (p) =>
          formatDate(
            p.habitable_buildings[0]
              ?.defects_condition_inspection_done_date as string,
            { locale, fallbackValue: '' },
          ),
      },
      {
        name: 'habitable_buildings.0.defects_has_water_damage',
        value: (p) =>
          yesNoUndefined(
            p.habitable_buildings[0]?.defects_has_water_damage,
            t,
            '',
          ),
        label: t('defects_has_water_damage'),
        description: (p) =>
          p.habitable_buildings[0]?.defects_water_damage_description,
      },
      {
        name: 'habitable_buildings.0.defects_has_moisture_damage',
        value: (p) =>
          yesNoUndefined(
            p.habitable_buildings[0]?.defects_has_moisture_damage,
            t,
            '',
          ),
        label: t('defects_has_moisture_damage'),
        description: (p) =>
          p.habitable_buildings[0]?.defects_moisture_damage_description,
      },
      {
        name: 'habitable_buildings.0.defects_has_fungus_damage',
        value: (p) =>
          yesNoUndefined(
            p.habitable_buildings[0]?.defects_has_fungus_damage,
            t,
            '',
          ),
        label: t('defects_has_fungus_damage'),
        description: (p) =>
          p.habitable_buildings[0]?.defects_fungus_damage_description,
      },
      {
        name: 'habitable_buildings.0.other_has_asbestos_mapping',
        value: (p) =>
          yesNoUndefined(
            p.habitable_buildings[0]?.other_has_asbestos_mapping,
            t,
            '',
          ),
        label: t('other_has_asbestos_mapping'),
      },
      {
        name: 'habitable_buildings.0.defects_has_other_damage',
        value: (p) =>
          yesNoUndefined(
            p.habitable_buildings[0]?.defects_has_other_damage,
            t,
            '',
          ),
        label: t('defects_has_other_damage'),
        description: (p) =>
          p.habitable_buildings[0]?.defects_other_damage_description,
      },
      {
        name: 'habitable_buildings.0.renovations_description',
        label: t('renovations_description'),
      },
      {
        name: 'habitable_buildings.0.defects_has_extra_attachment',
        value: (p) =>
          yesOrNull(p.habitable_buildings[0]?.defects_has_extra_attachment, t),
        label: t('defects_has_extra_attachment'),
      },
    ],
  };

  const buildingElectricityWaterInfo: DataSection = {
    title: t(
      'electricity_water_and_other_connections',
      'Sähkö-, vesi- yms. liittymät',
    ),
    items: [
      {
        name: 'habitable_buildings.0.other_has_electrical_connection',
        value: (p) =>
          yesOrNull(
            p.habitable_buildings[0]?.other_has_electrical_connection,
            t,
          ),
        label: t('other_has_electrical_connection'),
        description: (p) =>
          p.habitable_buildings[0]?.other_has_electrical_connection_description,
      },
      {
        name: 'habitable_buildings.0.other_has_energy_certificate',
        value: (p) =>
          yesNoUndefined(
            p.habitable_buildings[0]?.other_has_energy_certificate,
            t,
            '',
          ),
        label: t('other_has_energy_certificate'),
        description: (p) =>
          [
            p.habitable_buildings[0]?.other_energy_class
              ? `${t('energy_class', 'Energialuokka')}: ${
                  p.habitable_buildings[0]?.other_energy_class
                }`
              : undefined,
            p.habitable_buildings[0]?.other_energy_certificate_description,
          ]
            .filter(Boolean)
            .join('. '),
      },
      {
        name: 'habitable_buildings.0.other_has_underground_oil_tank',
        value: (p) =>
          yesOrNull(
            p.habitable_buildings[0]?.other_has_underground_oil_tank,
            t,
          ),
        label: t('other_has_underground_oil_tank'),
        description: (p) =>
          formatDate(
            p.habitable_buildings[0]
              ?.other_underground_oil_tank_inspection_date as string,
            { locale, fallbackValue: '' },
          ),
      },
      {
        name: 'habitable_buildings.0.other_connections_description',
        label: t('other_connections_description'),
      },
    ],
  };

  const buildingOtherCostsInfo: DataSection = {
    title: t('other_costs_description', 'Muut kustannukset'),
    items: [
      {
        name: 'habitable_buildings.0.cost_heating',
        value: (p) =>
          formatCurrency(p.habitable_buildings[0]?.cost_heating as number, {
            locale,
            fallbackValue: '',
          }),
        label: t('cost_heating'),
      },
      {
        name: 'habitable_buildings.0.cost_heating_electricity',
        value: (p) =>
          formatCurrency(
            p.habitable_buildings[0]?.cost_heating_electricity as number,
            {
              locale,
              fallbackValue: '',
            },
          ),
        label: t('cost_heating_electricity'),
      },
      {
        name: 'habitable_buildings.0.cost_water_and_waste_water',
        value: (p) =>
          formatCurrency(
            p.habitable_buildings[0]?.cost_water_and_waste_water as number,
            {
              locale,
              fallbackValue: '',
            },
          ),
        label: t('cost_water_and_waste_water'),
      },
      {
        name: 'habitable_buildings.0.cost_sanitation',
        value: (p) =>
          formatCurrency(p.habitable_buildings[0]?.cost_sanitation as number, {
            locale,
            fallbackValue: '',
          }),
        label: t('cost_sanitation'),
      },
      {
        name: 'habitable_buildings.0.cost_fixed',
        value: (p) =>
          formatCurrency(p.habitable_buildings[0]?.cost_fixed as number, {
            locale,
            fallbackValue: '',
          }),
        label: t('cost_fixed'),
      },
      {
        name: 'habitable_buildings.0.cost_additional_info',
        value: (p) => p.habitable_buildings[0]?.cost_additional_info,
        label: t('cost_additional_info'),
      },
    ],
  };

  const buildingNearbyServicesInfo: DataSection = {
    title: t('services_general_nearby', 'Lähipalvelut'),
    items: [
      {
        name: 'habitable_buildings.0.services_general',
        label: t('services_general'),
      },
      {
        name: 'habitable_buildings.0.services_schools',
        label: t('services_schools'),
      },
      {
        name: 'habitable_buildings.0.services_daycare',
        label: t('services_daycare'),
      },
      {
        name: 'habitable_buildings.0.services_public_transport',
        label: t('services_public_transport'),
      },
      {
        name: 'habitable_buildings.0.services_other',
        label: t('services_other'),
      },
    ],
  };

  const allSections = {
    propertyBasicInfo,
    propertyGroundInfo,
    propertyWaterAreaInfo,
    propertyLandUsePlanningInfo,
    propertyWaterPipeInfo,
    propertySewerInfo,
    propertyConnectionsInfo,
    propertyCostsInfo,
    propertyDebtsAndMortgagesInfo,
    propertyExtraInformationInfo,
    propertyDrivingInstructionsInfo,
    propertyEnvironmentAndGroundInfo,
    propertyForestDamageInfo,
    buildingBasicInfo,
    buildingKitchenInfo,
    buildingBathroomInfo,
    buildingSurfacesInfo,
    buildingOtherInventoryInfo,
    buildingOtherEquipmentInfo,
    buildingDamagesAndRenovationsInfo,
    buildingElectricityWaterInfo,
    buildingOtherCostsInfo,
    buildingNearbyServicesInfo,
  };

  const getComputedItemT = getComputedItem(t);
  const getComputedSectionT = getComputedSection(t);

  return {
    sections: allSections,
    processed: processor
      ? processor({
          sections: allSections,
          getComputedSection: getComputedSectionT,
          getComputedItem: getComputedItemT,
          isNonEmptyValue,
        })
      : undefined,
    getComputedSection: getComputedSectionT,
    getComputedItem: getComputedItemT,
    isNonEmptyValue,
  };
};
